import { type StockItemSummary } from "@artisancode/api-types";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { useRawMaterials } from "@/hooks/use-raw-materials";
import { getErrorMessage } from "@/lib/error-message";
import { newIdempotencyKey } from "@/lib/idempotency";
import { stockService } from "@/services/stock";
import { stockTransformationService } from "@/services/stock-transformation";

const today = () => new Date().toISOString().slice(0, 10);

/** State, shrinkage calculation and submit logic for the repacking dialog. */
export function useRepackingForm({
  open,
  onOpenChange,
  onSuccess,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}) {
  const [date, setDate] = useState(today);
  const [selectedRawMaterialId, setSelectedRawMaterialId] =
    useState<string>("");
  const [inputQtyStr, setInputQtyStr] = useState<string>("");
  // Blank = the full expected yield; fill in when the packages held less (spilled, short).
  const [outputQtyStr, setOutputQtyStr] = useState<string>("");
  const [generalNote, setGeneralNote] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  // One key per opened dialog: a retry after a network error reuses it, so the
  // server returns the draft it already created instead of making a second.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const idempotencyKey = useMemo(() => newIdempotencyKey("rpk"), [open]);

  // Fetch stock items to get available on_hand balance
  const { data: stocksRes, isLoading: isLoadingStocks } = useQuery({
    queryKey: ["stocks", "all-items"],
    queryFn: () => stockService.list({ per_page: 500 }),
    enabled: open,
  });

  // Only what sits sealed in the warehouse can be repacked: barang beli & langsung pakai.
  const { data: sourceRes } = useRawMaterials({
    kind: "purchased,direct",
    per_page: 500,
    is_active: true,
  });
  const sourceById = useMemo(
    () => new Map((sourceRes?.items ?? []).map((rm) => [rm.id, rm])),
    [sourceRes],
  );

  const rawMaterials = useMemo(() => {
    return (stocksRes?.items || []).filter(
      (i) => i.itemType === "raw_material" && sourceById.has(i.itemId),
    );
  }, [stocksRes, sourceById]);

  const selectedItem: StockItemSummary | undefined = useMemo(() => {
    return rawMaterials.find((i) => i.itemId === selectedRawMaterialId);
  }, [rawMaterials, selectedRawMaterialId]);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setDate(today());
      setSelectedRawMaterialId("");
      setInputQtyStr("");
      setOutputQtyStr("");
      setGeneralNote("");
    }
  }, [open]);

  // Calculations
  const inputQty = parseFloat(inputQtyStr) || 0;
  const isInputGreaterThanOnHand = selectedItem
    ? inputQty > selectedItem.onHandQty
    : false;

  const source = sourceById.get(selectedRawMaterialId);
  const opensInto = source?.kind === "purchased" ? source.ingredient : null;
  const expectedQty =
    opensInto && source?.packQty ? inputQty * source.packQty : inputQty;
  const outputUom = opensInto?.uom?.symbol ?? selectedItem?.uomName ?? "unit";
  // Bar stock lives on the output: the ingredient for barang beli, the item itself otherwise.
  const outputStock = (stocksRes?.items ?? []).find(
    (i) =>
      i.itemType === "raw_material" &&
      i.itemId === (opensInto?.id ?? selectedRawMaterialId),
  );
  const outputQty = outputQtyStr ? parseFloat(outputQtyStr) || 0 : expectedQty;
  const shrinkageQty = Math.max(0, expectedQty - outputQty);
  const isOutputOverExpected = outputQty > expectedQty;

  const canSubmit =
    !isSubmitting &&
    !!selectedRawMaterialId &&
    inputQty > 0 &&
    outputQty > 0 &&
    !isOutputOverExpected;

  const submit = async (completeImmediately: boolean) => {
    if (!selectedRawMaterialId) {
      toast.error("Pilih bahan baku asal terlebih dahulu.");
      return;
    }
    if (inputQty <= 0) {
      toast.error("Jumlah input harus lebih besar dari 0.");
      return;
    }

    try {
      setIsSubmitting(true);

      const payload = {
        date,
        sourceRawMaterialId: selectedRawMaterialId,
        inputQty,
        outputQty: outputQtyStr ? outputQty : undefined,
        note: generalNote.trim() || undefined,
      };

      const created = await stockTransformationService.createRepack(
        payload,
        idempotencyKey,
      );

      if (completeImmediately && created.id) {
        await stockTransformationService.complete(created.id);
        toast.success(
          `Repacking ${created.code} berhasil diselesaikan. Stok dipindahkan ke wadah repacking bar!`,
        );
      } else {
        toast.success(`Draft repacking ${created.code} berhasil disimpan.`);
      }

      onSuccess();
      onOpenChange(false);
    } catch (err) {
      toast.error(getErrorMessage(err, "Gagal memproses dokumen repacking."));
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    date,
    setDate,
    selectedRawMaterialId,
    setSelectedRawMaterialId,
    inputQtyStr,
    setInputQtyStr,
    outputQtyStr,
    setOutputQtyStr,
    opensInto,
    outputStock,
    expectedQty,
    outputUom,
    shrinkageQty,
    isOutputOverExpected,
    generalNote,
    setGeneralNote,
    isSubmitting,
    isLoadingStocks,
    rawMaterials,
    selectedItem,
    inputQty,
    isInputGreaterThanOnHand,
    canSubmit,
    submit,
  };
}
